import _ from 'lodash';

import { makeReactive } from '@dha/make-reactive';
import GeolocationService from '@/services/GeolocationService';
import PDFModel from './PDFModel';
import PagesModel from './PagesModel';
import LinksModel from './LinksModel';
import MapModel from './MapModel';
import RegionModel from './RegionModel';
import ControlsModel from './ControlsModel';
import MobileNavModel from './MobileNavModel';
import CategoryModel from './CategoryModel';

import { deepLinkModel } from './deeplink';

/* eslint-disable class-methods-use-this */
export class AppModel {
    constructor() {
        // App Model State
        this.comparison = 'AB';
        this.comparisons = Object.freeze(['Zone', 'AB']);
        this.loaded = false;
        this.showPDF = false;
        // Data
        this.config = {};
        this.metricsConfig = {};
        this.postalLookup = [];
        this.zoneLookup = [];
        this.lgaClassLookup = [];
        this.lgaGeojson = null;
        this.municipalities = [];

        // Derived Data used by Section Models
        this.metrics = [];
        this.metricValues = [];

        // Derived Data used by Pages Model
        this.pages = [];
    }

    initData({
        metricsConfig,
        appConfig,
        postalLookup,
        zoneLookup,
        lgaClassLookup,
        populationAgeGender,
        lgaGeojson,
        cdi,
        populationGrowth,
        education,
        metricValues,
        immigration,
        municipalities,
        metrics
    }) {
        this.metricsConfig = metricsConfig;
        // DataService provides frozen objects.  No need to freeze if using directly.
        this.config = appConfig;
        this.postalLookup = postalLookup;
        this.zoneLookup = zoneLookup;
        this.lgaClassLookup = lgaClassLookup;
        this.lgaGeojson = lgaGeojson;
        this.municipalities = municipalities;

        // Derived Data for Models needing metrics
        const combinedMetricValues = [
            ...cdi,
            ...education,
            ...metricValues,
            ...populationGrowth,
            ...populationAgeGender,
            ...immigration
        ].map(({ geographyCode, ...rest }) => ({
            geographyCode,
            geographyName: this.regionModel.geographyNameLookup[geographyCode],
            urbanRuralClass: this.regionModel.urbanRuralClassLookup[geographyCode],
            ...rest
        }));
        this.metricValues = Object.freeze(combinedMetricValues);
        // TODO: Is this the right place to be parsing this?
        //
        // Transform string config options to a usable format
        // Domain strings like: '0,100' should be converted to arrays: [0, 100]
        // formatter strings like 'float1' or 'percent' should be converted to formatter functions
        const mappedMetrics = metrics.map(({ domain: domainString, formatter: formatString, ...rest }) => {
            const domainRe = /(\d+)\D+(\d+)/;
            let domain;
            if (domainRe.test(domainString) && domainRe.exec(domainString).length === 3) {
                const [, min, max] = domainRe.exec(domainString);
                domain = [+min, +max];
            } else if (domainString === 'max') {
                domain = 'max';
            }
            if (domain) {
                // eslint-disable-next-line no-param-reassign
                rest.domain = domain;
            }
            // TODO: Refactor fomratters.
            // All of these have the -0 bug, potentially
            const formatters = {
                thousands: v => `${(v / 1000).toFixed(0)}K`,
                money: v => `$${(v / 1000).toFixed(0)}K`,
                float1: v => (v.toFixed ? v.toFixed(1) : v),
                float0: v => (v.toFixed ? v.toFixed(0) : v),
                percent: v => (v * 100).toFixed(0)
            };
            const format = formatters[formatString] || formatters.percent;

            return {
                format,
                ...rest,
                id: _.kebabCase(rest.metric),
            };
        });

        this.metrics = Object.freeze(_.filter(mappedMetrics, (metric) => !metric.exclude));

        // Maps config to pages in Pages Model
        this.pages = this.mapPages();
        this.loaded = true;
    }

    get categories() {
        return this.config.categories;
    }

    // Region dependency for the region and comparison props to react to
    get region() {
        return this.regionModel.region;
    }

    get localName() {
        return this.region && this.region.localName;
    }

    get comparisonName() {
        return this.region && this.region.zoneName;
    }

    get geographyCode() {
        return this.region && this.region.localCode || 'AB';
    }

    get urbanRuralClass() {
        return this.region && this.region.urbanRuralClass;
    }

    get urbanRuralLabel() {
        const urc = this.urbanRuralClass;
        return urc.endsWith('Area') ? urc : `${urc} Area`;
    }

    get comparisonGeographyCode() {
        switch (this.comparison) {
        case 'AB':
            return 'AB';
        case 'Zone':
            return this.region && this.region.zoneCode || 'AB';
        default:
            return '';
        }
    }

    get zoneGeographyCode() {
        return this.region ? this.region.localCode.split('.')[0] : null;
    }

    get abGeographyCode() {
        return 'AB';
    }

    get comparisonText() {
        switch (this.comparison) {
        case 'AB':
            return 'in AB';
        case 'Zone':
            return 'in Zone';
        default:
            return '';
        }
    }

    get metricsBySection() {
        const sections = _(this.metrics)
            .groupBy('section')
            .mapValues(arr => _.sortBy(arr, 'sortIndex'))
            .value();

        return Object.freeze({
            // Placeholder while we don't have data for stress
            stress: sections.mentalHealth,
            ...sections
        });
    }

    get valuesByMetric() {
        return Object.freeze(_.groupBy(this.metricValues, 'metric'));
    }

    get metricsById() {
        return Object.freeze(_.keyBy(this.metrics, 'metric'));
    }

    get sectionConfig() {
        return this.config.sections;
    }

    get mapModel() {
        return makeReactive(new MapModel(this.regionModel));
    }

    get pagesModel() { return makeReactive(new PagesModel(this, { pages: this.pages })); }

    // Current hard-coded because we need a new vue component per link
    get linksModel() {
        return makeReactive(new LinksModel(this.config.sidebarLinks));
    }

    get additionalReferences() {
        return this.config.references.additional;
    }

    get regionModel() {
        if (!this.singletonRegionModel) {
            const geoService = makeReactive(new GeolocationService());
            geoService.init();
            this.singletonRegionModel = makeReactive(new RegionModel(this, geoService));
        }
        return this.singletonRegionModel;
    }

    get mobileNavModel() {
        const mobileNavModel = makeReactive(new MobileNavModel(this));
        mobileNavModel.init();
        return mobileNavModel;
    }

    get controlsModel() {
        return makeReactive(new ControlsModel(this));
    }

    get pdfModel() {
        return makeReactive(new PDFModel(this, this.config.pdf));
    }

    // Creates category model from page, used by generic-tab
    createCategoryModel({ id, sections, ...config }) {
        return makeReactive(new CategoryModel(this, { id, sections, ...config }));
    }

    // Prepends prefix to icons
    getIcon(icon) {
        return icon && `${this.config.icons.prefix}${icon}`;
    }

    get year() {
        return this.metricsConfig.year;
    }

    get version() {
        return `Darkhorse Analytics - ${process.env.VUE_APP_NAME} v${process.env.VUE_APP_VERSION}`;
    }

    get clientInfo() {
        return `${window.location.href} with ${navigator.userAgent}`;
    }

    // Pages are the model that determines the content in the content page.
    // as well as in the Sidebar menu
    // Each page needs a title, id and can contain a pages array
    mapPages() {
        if (!this.config.categories) {
            return [];
        }

        const categoryPages = this.config.categories.map(category => (
            { ...category,
                type: 'generic-tab',
                pages: category.sections.map(section => (
                    { ...this.config.sections[section]
                    })),
                model: this.createCategoryModel(category)
            }));
        const overviewConfig = this.config.overview;

        const overviewPage = { ...overviewConfig,
            type: 'scrolly-page',
            // NOTE: map this as pages if we want these to be links in the sidemenu
            categories: overviewConfig.categories.map((category) => {
                const cat = _.find(this.config.categories, { id: category.id });
                const sections = category.sections || cat.sections;
                return {
                    ...cat,
                    ...category,
                    id: `${category.id}Overview`,
                    title: cat ? cat.title : '-',
                    details: sections.map(catSection => (
                        { ...this.config.sections[catSection],
                            icon: this.getIcon(this.config.sections[catSection].icon)
                        })),
                };
            })
        };
        const conclusionPage = { ...this.config.conclusion, type: 'conclusion-tab' };
        const pages = _.concat([], overviewPage, categoryPages, conclusionPage);
        // const pages = _.concat([], overviewPage, categoryPages);
        return Object.freeze(pages);
    }
}

const model = makeReactive(new AppModel());
// Add any deeplinkable state here
// TODO: Just make a DeeplinkController and instantiate it here
model.comparison = deepLinkModel(model, 'comparison');
model.regionModel.localCode = deepLinkModel(model.regionModel, 'localCode');

export default model;
